// frontend/src/lib/deposit-amount.ts
// CF-DEPOSIT-CUSTOM-AMOUNT-V1 (PL #7245). Pure, fail-loud derivation of the deposit
// amount from the two UI sources (a quick chip OR a free-form custom entry). Kept in
// lib/ (no React) so this critical money logic is unit-testable without jsdom — same
// separation as lib/ton-format.ts.

import { parseTonToNano } from './ton-format';
import { fmtTon } from './ui-derivers';
import { translate } from '../i18n';

export const NANO_PER_TON = 1_000_000_000n;

// Custodial deposits credit whatever TON actually lands (matched by invoice memo; the
// invoice endpoint carries no amount), so there is NO protocol min/max — these are UX
// guards only. MIN = one 0.5 TON minimum flip; MAX = a fat-finger guard (a player can
// always deposit again to add more). Tuned UX values, not protocol constants.
export const MIN_DEPOSIT_NANO = 500_000_000n;        // 0.5 TON
export const MAX_DEPOSIT_NANO = 1_000_000_000_000n;  // 1000 TON

export type DepositAmount =
    | { kind: 'ok'; nano: bigint }
    | { kind: 'error'; message: string }
    | { kind: 'empty' };

// A non-empty custom field WINS over a chip (the two are kept mutually exclusive by the
// component handlers). FAIL-LOUD (#7233): a malformed / under-min / over-max entry
// yields {kind:'error'} — the value is NEVER coerced into a plausible deposit. The exact
// nano comes from parseTonToNano's string math (no float), so what is sent on-chain is
// exactly what the player typed.
export function deriveDepositAmount(chip: number | null, custom: string): DepositAmount {
    const c = custom.trim();
    if (c !== '') {
        const r = parseTonToNano(c);
        if (!r.ok) {
            return {
                kind: 'error',
                message: r.reason === 'too_precise'
                    ? translate('deposit.amount_error.too_precise')
                    : translate('deposit.amount_error.invalid_amount'),
            };
        }
        if (r.nano < MIN_DEPOSIT_NANO) return { kind: 'error', message: translate('deposit.amount_error.below_min', { min: fmtTon(MIN_DEPOSIT_NANO) }) };
        if (r.nano > MAX_DEPOSIT_NANO) return { kind: 'error', message: translate('deposit.amount_error.above_max', { max: fmtTon(MAX_DEPOSIT_NANO) }) };
        return { kind: 'ok', nano: r.nano };
    }
    if (chip !== null) return { kind: 'ok', nano: BigInt(chip) * NANO_PER_TON };
    return { kind: 'empty' };
}
